import React from "react";

export default function AIPowerChart() {
  // Define TypeScript types
  type AnnotationValue = {
    value: number;
    color: string;
    label: string;
  };

  type Annotation = {
    x: number;
    y: number;
    text: string;
    values: AnnotationValue[];
  };

  type ShareAnnotation = {
    x: number;
    y: number;
    value: number;
    label: string;
  };

  // Set dimensions and margins (matching the previous charts)
  const width = 1000;
  const height = 700;
  const margin = { top: 40, right: 40, bottom: 50, left: 120 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  // Parse power values (converting MW/GW to a consistent unit - we'll use GW)
  const parsePower = (strValue: string): number => {
    if (strValue.includes("MW")) {
      return parseFloat(strValue.replace("MW", "")) / 1000; // Convert MW to GW
    } else {
      return parseFloat(strValue.replace("GW", ""));
    }
  };

  // Parse percentage values
  const parsePercentage = (strValue: string): number => {
    return parseFloat(strValue.replace("%", ""));
  };

  // Power data
  const leaderStr: string[] = [
    "460MW","520MW","587MW","663MW","748MW","845MW","955MW","1GW","1.22GW","1.38GW","1.55GW","1.75GW",
    "1.98GW","2.2GW","2.3GW","2.5GW","2.8GW","3.0GW","3.3GW","3.6GW","3.9GW","4.2GW","4.6GW","5.0GW",
    "5.4GW","5.7GW","6.0GW","6.4GW","6.8GW","7.2GW","7.6GW","8.0GW","8.5GW","9.0GW","9.5GW","10.1GW","10.7GW"
  ];

  const globalStr: string[] = [
    "9.9GW","10.6GW","11.3GW","12.1GW","12.9GW","13.7GW","14.6GW","15.5GW","16.5GW","17.5GW","18.6GW","19.7GW",
    "20.9GW","22.2GW","23.5GW","24.9GW","26.4GW","27.9GW","29.5GW","31.1GW","32.7GW","34.3GW","35.8GW","37.4GW",
    "39.0GW","40.6GW","42.2GW","43.8GW","45.4GW","47.0GW","48.9GW","50.6GW","52.4GW","54.3GW","56.1GW","58.0GW","60GW"
  ];

  const usStr: string[] = [
    "6.8GW","7.2GW","7.7GW","8.2GW","8.7GW","9.3GW","9.8GW","10.7GW","11.7GW","12.8GW","13.9GW","15.0GW",
    "16.1GW","17.3GW","18.5GW","19.7GW","21.0GW","22.3GW","23.6GW","24.9GW","26.2GW","27.5GW","28.8GW","30.1GW",
    "31.5GW","32.9GW","34.4GW","35.9GW","37.4GW","38.9GW","40.4GW","42.0GW","43.5GW","45.0GW","46.5GW","48.2GW","50GW"
  ];

  const shareStr: string[] = [
    "0.5%","0.56%","0.60%","0.65%","0.70%","0.75%","0.80%","0.86%","0.93%","1.00%","1.07%","1.15%",
    "1.23%","1.31%","1.38%","1.46%","1.5%","1.64%","1.74%","1.84%","1.94%","2.06%","2.18%","2.30%",
    "2.5%","2.52%","2.61%","2.71%","2.80%","2.90%","3.01%","3.11%","3.22%","3.34%","3.45%","3.58%","3.5%"
  ];

  // Parse to numeric values
  const leaderPower: number[] = leaderStr.map(parsePower);
  const globalPower: number[] = globalStr.map(parsePower);
  const usPower: number[] = usStr.map(parsePower);
  const sharePercentage: number[] = shareStr.map(parsePercentage);

  // Create date labels
  const generateDates = (): string[] => {
    const dates: string[] = [];
    let currentDate = new Date(2025, 0, 1); // Start from January 2025
    
    for (let i = 0; i < leaderPower.length; i++) {
        dates.push(currentDate.toLocaleDateString('en-US', { 
            month: 'short', 
            year: 'numeric' 
          }));
          currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    return dates;
  };

  const dates: string[] = generateDates();

  // Colors (matching the style from previous charts)
// Colors matching the GPU deployment chart
// New color scheme using cooler colors
// New color scheme with green shades and blue line
const colors = {
    global: "#508050",  // Very light green for global area
    us: "#003000",      // Dark green from GPU chart
    leader: "#0000AA",  // Blue for leading company line
    share: "#AA0000",   // Red from GPU chart for share percentage
    grid: "#ddd",       // Light gray for grid
    text: "#333333",    // Dark gray for text
    axis: "#444444"     // Dark gray for axis
  };

//   const colors = {
//     h100: "#000100",     // Dark gray/black (using compute color from original)
//     gb200: "#508050",    // Medium green (using humanAlgo color from original)
//     gb300: "#004000",    // Dark green (using aiAlgo color from original)
//     vr200: "#AA0000",    // Red (new color)
//     inhouse: "#0000AA",  // Blue (new color)
//     axis: "#444444",     // Dark gray for axis
//     text: "#333333"      // Dark gray text

  // Define which tick indices to show on x-axis (to avoid crowding)
  const xTickIndices: number[] = [0, 6, 12, 18, 24, 30, 36];

  // Define annotation points
  const annotations: Annotation[] = [
    { 
      x: 0, 
      y: globalPower[0], 
      text: "Dec 2024", 
      values: [
        { value: globalPower[0], color: colors.global, label: `10 GW` },
        { value: usPower[0], color: colors.us, label: `7 GW` },
        { value: leaderPower[0], color: colors.leader, label: `0.5 GW` }
      ] 
    },
    { 
      x: 30, 
      y: globalPower[12], 
      text: "Dec 2027", 
      values: [
        { value: globalPower[24], color: colors.global, label: `60 GW` },
        { value: usPower[24], color: colors.us, label: `50 GW` },
        { value: leaderPower[24], color: colors.leader, label: `10 GW` }
      ] 
    }
  ];

  // Add share percentage annotation points
  const shareAnnotations: ShareAnnotation[] = [
    { x: 0, y: usPower[0], value: sharePercentage[0], label: `${sharePercentage[0]}%` },
    { x: 12, y: usPower[12], value: sharePercentage[12], label: `${sharePercentage[16]}%` },
    { x: 24, y: usPower[24], value: sharePercentage[24], label: `${sharePercentage[24]}%` },
    { x: 35, y: usPower[35], value: sharePercentage[36], label: `${sharePercentage[36]}%` },
  ];

  // Scales
  const xScale = (index: number): number => {
    return margin.left + (index / (dates.length - 1)) * innerWidth;
  };

  const yScale = (value: number): number => {
    const maxValue = 65; // Just above our maximum data point
    return margin.top + innerHeight - (value / maxValue) * innerHeight;
  };

  // Generate area paths for stacked areas
  const generateAreaPath = (data: number[]): string => {
    // Draw the top curve from left to right
    let path = data.map((value, i) => 
      `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(value)}`
    ).join(' ');
    
    // Then draw the bottom line from right to left
    path += ` L ${xScale(data.length - 1)} ${yScale(0)} L ${xScale(0)} ${yScale(0)} Z`;
    
    return path;
  };

  // Generate line paths
  const generateLinePath = (data: number[]): string => {
    return data.map((value, i) => 
      `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(value)}`
    ).join(' ');
  };

  // Create paths
  const globalAreaPath: string = generateAreaPath(globalPower);
  const usAreaPath: string = generateAreaPath(usPower);
  const leaderLinePath: string = generateLinePath(leaderPower);
  
  // Y-axis ticks at 10GW intervals
  const yTicks: number[] = [0, 10, 20, 30, 40, 50, 60];

  return (
    <div className="w-full relative overflow-hidden mt-8 mb-8">
      <svg
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        viewBox={`0 0 ${width} ${height}`}
      >
        {/* Chart Title */}
        <text
          x={margin.left}
          y={40}
          textAnchor="start"
          fontSize="32"
          fontFamily="sans-monospace"
          className="text-[32px] font-mono md:text-[32px] sm:text-[32px]"
          fill={colors.text}
        >
          Global, US, and Leading Company AI Power
        </text>

        {/* Y-axis Label */}
        <text
          transform={`rotate(-90)`}
          y={10}
          x={-height / 2}
          dy="1em"
          textAnchor="middle"
          className="text-[22px] font-mono md:text-[22px] sm:text-[22px]"
          fill={colors.text}
        >
          Power Capacity Requirement
        </text>

        {/* X-axis ticks and labels */}
        {xTickIndices.map((index) => {
          const x = xScale(index);
          return (
            <g key={`x-tick-${index}`}>
              <line 
                x1={x} 
                y1={height - margin.bottom} 
                x2={x} 
                y2={margin.top} 
                stroke={colors.axis}
                strokeWidth="0.5" 
                strokeDasharray="5,5"
                opacity="0.15"
              />
              <text 
                x={x} 
                y={height - margin.bottom / 2} 
                textAnchor="middle" 
                className="text-[16px] font-mono md:text-[16px] sm:text-[16px]"
                fill={colors.text}
              >
                {dates[index]}
              </text>
            </g>
          );
        })}

        {/* Horizontal grid lines */}
        {yTicks.map((tick, i) => (
          <g key={`grid-${i}`}>
            <line 
              x1={margin.left} 
              y1={yScale(tick)} 
              x2={width - margin.right} 
              y2={yScale(tick)} 
              stroke={colors.grid} 
              strokeWidth="0.8" 
              strokeDasharray="5,5"
            />
            
            <text 
              x={margin.left - 10} 
              y={yScale(tick) + 4} 
              textAnchor="end" 
              className="text-[18px] font-mono md:text-[18px] sm:text-[18px]"
              fill={colors.text}
            >
              {tick === 0 ? "0" : `${tick} GW`}
            </text>
          </g>
        ))}

        {/* Stacked areas */}
        <path 
          d={globalAreaPath} 
          fill={colors.global} 
          fillOpacity={.5} 
          stroke="none"
        />
        
        <path 
          d={usAreaPath} 
          fill={colors.us} 
          fillOpacity={.9} 
          stroke="none"
        />

        {/* Leading company line */}
        <path
          d={leaderLinePath}
          fill="none"
          stroke={colors.leader}
          strokeWidth="4"
        />

        {/* Data Points for leading company */}
        {leaderPower.map((value, i) => (
          <circle
            key={`leader-point-${i}`}
            cx={xScale(i)}
            cy={yScale(value)}
            r="5"
            fill={colors.leader}
          />
        ))}

        {/* Share percentage points */}
        {shareAnnotations.map((anno, i) => (
          <g key={`share-anno-${i}`}>
            <circle
              cx={xScale(anno.x)}
              cy={yScale(anno.y)} // Position them at different heights for visibility
              r="6"
              fill={colors.share}
            />
            <text
              x={xScale(anno.x) + 15 * ((i===3)?-2:1)}
              y={yScale(anno.y) + 15 * ((i===3)?2.5:1)} 
              className="text-[18px] font-mono"
              fill={"#cccccc"}
            >
              {anno.label}
            </text>
          </g>
        ))}

        {/* Legend */}
        <g transform={`translate(${margin.left+15}, 75)`}>
          {/* Global AI Power */}
          <rect x="0" y="0" width="20" height="20" fill={colors.global} />
          <text x="30" y="15" fill={colors.text} className="text-[16px] font-mono">Global AI power</text>
          
          {/* US AI Power */}
          <rect x="0" y="30" width="20" height="20" fill={colors.us} />
          <text x="30" y="45" fill={colors.text} className="text-[16px] font-mono">US AI power</text>
          
          {/* Leading Company */}
          <line x1="0" y1="70" x2="20" y2="70" stroke={colors.leader} strokeWidth="2.5" />
          <circle cx="10" cy="70" r="4" fill={colors.leader} />
          <text x="30" y="75" fill={colors.text} className="text-[16px] font-mono">Leading US AI Company</text>
          
          {/* Share */}
          <circle cx="10" cy="100" r="6" fill="#aa0000" />
          <text x="30" y="105" fill={colors.text} className="text-[16px] font-mono">Share of US Power on AI</text>
        </g>

        {/* Annotations */}
        {annotations.map((anno, i) => {
          return (
            <g key={`annotation-${i}`} transform={`translate(${xScale(anno.x)}, ${yScale(anno.y) - 120})`}>
              <rect
                x="20"
                y="-50"
                width="100"
                height="110"
                fill="white"
                stroke={colors.grid}
                strokeWidth="1"
                rx="5"
                ry="5"
              />
              <text
                x="70"
                y="-30"
                textAnchor="middle"
                className="text-[16px] font-mono"
                fill={colors.text}
              >
                {anno.text}
              </text>
              {anno.values.map((val, j) => (
                <text
                  key={`anno-val-${i}-${j}`}
                  x="70"
                  y={-10 + j*25}
                  textAnchor="middle"
                  className="text-[16px] font-mono"
                  fill={val.color}
                  fontWeight="bold"
                >
                  {val.label}
                </text>
              ))}
            </g>
          );
        })}
      </svg>
    </div>
  );
}